import { batch, effect } from '@preact/signals';
import type { FrameOutput } from '@meteoradar/shared/contract/frame.schema';
import type { ProductCode } from '@meteoradar/shared/enums/product-code';
import { MS_PER_SECOND } from '@meteoradar/shared/time/units';
import { type ApiClient } from '../api/client.js';
import { BASE_FRAME_MS, LAST_FRAME_PAUSE_MS, PRELOAD_CONCURRENCY, REFRESH_INTERVAL_SECONDS } from '../config.js';
import {
  cursor,
  error,
  frames,
  isAtLatest,
  latestFrame,
  loadedCount,
  loading,
  pendingFrame,
  playing,
  product,
  products,
  resetView,
  speed,
  windowHours,
} from '../state/store.js';
import { type BitmapCache } from './bitmap-cache.js';

const DEFAULT_BASE_FRAME_MS = BASE_FRAME_MS;
const DEFAULT_LAST_FRAME_PAUSE_MS = LAST_FRAME_PAUSE_MS;
const DEFAULT_REFRESH_MS = REFRESH_INTERVAL_SECONDS * MS_PER_SECOND;

export type PlayerOptions = {
  baseFrameMs?: number;
  lastFramePauseMs?: number;
  refreshMs?: number;
};

export type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
};

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: handle => clearInterval(handle as ReturnType<typeof setInterval>),
};

const nearestIndex = (list: FrameOutput[], ts: string | null): number => {
  if (list.length === 0) {
    return 0;
  }
  if (ts === null) {
    return list.length - 1;
  }
  const target = Date.parse(ts);
  let best = list.length - 1;
  let bestDistance = Number.POSITIVE_INFINITY;
  list.forEach((frame, index) => {
    const distance = Math.abs(Date.parse(frame.ts) - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
};

export class PlayerController<T = ImageBitmap> {
  private readonly baseFrameMs: number;
  private readonly lastFramePauseMs: number;
  private readonly refreshMs: number;
  private tickHandle: unknown = null;
  private refreshHandle: unknown = null;
  private loadToken = 0;
  private disposeEffects: (() => void)[] = [];

  public constructor(
    private readonly api: ApiClient,
    private readonly cache: BitmapCache<T>,
    options: PlayerOptions = {},
    private readonly timers: Timers = defaultTimers,
  ) {
    this.baseFrameMs = options.baseFrameMs ?? DEFAULT_BASE_FRAME_MS;
    this.lastFramePauseMs = options.lastFramePauseMs ?? DEFAULT_LAST_FRAME_PAUSE_MS;
    this.refreshMs = options.refreshMs ?? DEFAULT_REFRESH_MS;
  }

  public async start(): Promise<void> {
    try {
      products.value = await this.api.getProducts();
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : String(cause);
    }
    let lastKey = '';
    this.disposeEffects.push(effect(() => {
      const key = `${product.value}|${windowHours.value}`;
      if (key === lastKey) {
        return;
      }
      lastKey = key;
      void this.reload();
    }));
    this.refreshHandle = this.timers.setInterval(() => {
      void this.refresh();
    }, this.refreshMs);
    this.scheduleTick();
  }

  public stop(): void {
    this.disposeEffects.forEach(dispose => dispose());
    this.disposeEffects = [];
    if (this.tickHandle !== null) {
      this.timers.clearTimeout(this.tickHandle);
      this.tickHandle = null;
    }
    if (this.refreshHandle !== null) {
      this.timers.clearInterval(this.refreshHandle);
      this.refreshHandle = null;
    }
  }

  public async fetchWindow(hours: number): Promise<FrameOutput[]> {
    return await this.api.getFrames(product.value, hours);
  }

  public async prefetch(list: FrameOutput[], onProgress?: (done: number, total: number) => void): Promise<void> {
    const urls = new Set<string>();
    for (const frame of list) {
      if (frame.baseUrl != null) {
        urls.add(frame.baseUrl);
      }
      urls.add(frame.url);
    }
    const queue = [ ...urls ].filter(url => !this.cache.has(url));
    const total = queue.length;
    let done = 0;
    const worker = async (): Promise<void> => {
      for (let url = queue.shift(); url !== undefined; url = queue.shift()) {
        try {
          await this.cache.get(url);
        } catch {
          continue;
        } finally {
          done += 1;
          onProgress?.(done, total);
        }
      }
    };
    await Promise.all(Array.from({ length: PRELOAD_CONCURRENCY }, worker));
  }

  public async loadLayers(frame: FrameOutput): Promise<T[]> {
    const layers: T[] = [];
    if (frame.baseUrl != null) {
      layers.push(await this.cache.get(frame.baseUrl));
    }
    layers.push(await this.cache.get(frame.url));
    return layers;
  }

  public bitmapFor(frame: FrameOutput | null): T | null {
    return frame === null ? null : this.cache.peek(frame.url);
  }

  public baseBitmapFor(frame: FrameOutput | null): T | null {
    return frame?.baseUrl == null ? null : this.cache.peek(frame.baseUrl);
  }

  public layersFor(frame: FrameOutput | null): (T | null)[] {
    if (frame === null) {
      return [];
    }
    if (frame.baseUrl == null) {
      return [ this.bitmapFor(frame) ];
    }
    const base = this.baseBitmapFor(frame);
    return base === null ? [] : [ base, this.bitmapFor(frame) ];
  }

  public play(): void {
    playing.value = true;
  }

  public pause(): void {
    playing.value = false;
  }

  public toggle(): void {
    playing.value = !playing.value;
  }

  public next(): void {
    this.pause();
    this.seek(cursor.value + 1);
  }

  public prev(): void {
    this.pause();
    this.seek(cursor.value - 1);
  }

  public first(): void {
    this.pause();
    this.seek(0);
  }

  public last(): void {
    this.seek(frames.value.length - 1);
  }

  public nextProduct(): void {
    const codes = products.value.map(item => item.code);
    const index = codes.indexOf(product.value);
    const next = codes[(index + 1) % codes.length];
    if (next !== undefined) {
      this.selectProduct(next);
    }
  }

  public seek(index: number): void {
    const count = frames.value.length;
    if (count === 0) {
      return;
    }
    cursor.value = (index % count + count) % count;
    void this.ensureLoaded(cursor.value);
  }

  public setSpeed(value: number): void {
    speed.value = value;
  }

  public selectProduct(code: ProductCode): void {
    if (product.value !== code) {
      resetView();
    }
    product.value = code;
  }

  public selectWindow(hours: number): void {
    windowHours.value = hours;
  }

  public async reload(): Promise<void> {
    const token = this.nextToken();
    const previousTs = frames.value[cursor.value]?.ts ?? null;
    const wasAtLatest = isAtLatest.value || frames.value.length === 0;
    loading.value = true;
    error.value = null;
    try {
      const list = await this.api.getFrames(product.value, windowHours.value);
      if (token !== this.loadToken) {
        return;
      }
      const requested = pendingFrame.value;
      batch(() => {
        frames.value = list;
        cursor.value = requested !== null
          ? nearestIndex(list, requested.toISOString())
          : wasAtLatest ? Math.max(0, list.length - 1) : nearestIndex(list, previousTs);
        loadedCount.value = list.filter(frame => this.cache.has(frame.url)).length;
        pendingFrame.value = null;
      });
      this.rescheduleTick();
      await this.preload(list, token, cursor.value);
    } catch (cause) {
      if (token === this.loadToken) {
        error.value = cause instanceof Error ? cause.message : String(cause);
      }
    } finally {
      if (token === this.loadToken) {
        loading.value = false;
      }
    }
  }

  public async refresh(): Promise<void> {
    try {
      const latest = await this.api.getLatest(product.value);
      const current = latestFrame.value;
      if (latest === null || (current !== null && latest.ts <= current.ts)) {
        return;
      }
      const wasAtLatest = isAtLatest.value;
      const list = await this.api.getFrames(product.value, windowHours.value);
      const token = this.nextToken();
      const previousTs = frames.value[cursor.value]?.ts ?? null;
      batch(() => {
        frames.value = list;
        cursor.value = wasAtLatest ? Math.max(0, list.length - 1) : nearestIndex(list, previousTs);
        loadedCount.value = list.filter(frame => this.cache.has(frame.url)).length;
      });
      this.rescheduleTick();
      await this.preload(list, token, cursor.value);
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : String(cause);
    }
  }

  private nextToken(): number {
    this.loadToken += 1;
    return this.loadToken;
  }

  private async preload(list: FrameOutput[], token: number, startIndex: number): Promise<void> {
    const order = [ ...list.slice(startIndex), ...list.slice(0, startIndex).reverse() ];
    const baseUrls = new Set(list.map(frame => frame.baseUrl).filter((url): url is string => url != null));
    for (const url of baseUrls) {
      try {
        await this.cache.get(url);
      } catch {
        continue;
      }
    }
    const queue = order.filter(frame => !this.cache.has(frame.url));
    const worker = async (): Promise<void> => {
      for (let frame = queue.shift(); frame !== undefined; frame = queue.shift()) {
        if (token !== this.loadToken) {
          return;
        }
        try {
          await this.cache.get(frame.url);
          if (token === this.loadToken) {
            loadedCount.value += 1;
          }
        } catch {
          continue;
        }
      }
    };
    await Promise.all(Array.from({ length: PRELOAD_CONCURRENCY }, worker));
  }

  private async ensureLoaded(index: number): Promise<void> {
    const frame = frames.value[index];
    if (frame === undefined) {
      return;
    }
    try {
      if (frame.baseUrl != null && !this.cache.has(frame.baseUrl)) {
        await this.cache.get(frame.baseUrl);
      }
      if (!this.cache.has(frame.url)) {
        await this.cache.get(frame.url);
        loadedCount.value += 1;
      }
    } catch {
      return;
    }
  }

  private rescheduleTick(): void {
    if (this.tickHandle !== null) {
      this.timers.clearTimeout(this.tickHandle);
      this.tickHandle = null;
    }
    this.scheduleTick();
  }

  private scheduleTick(): void {
    const atLast = isAtLatest.value;
    const delay = this.baseFrameMs / speed.value + (atLast ? this.lastFramePauseMs : 0);
    this.tickHandle = this.timers.setTimeout(() => {
      void this.tick();
    }, delay);
  }

  private async tick(): Promise<void> {
    this.tickHandle = null;
    if (playing.value && frames.value.length > 1 && !loading.value) {
      const nextIndex = (cursor.value + 1) % frames.value.length;
      const frame = frames.value[nextIndex];
      if (frame !== undefined) {
        if (!this.cache.has(frame.url) || (frame.baseUrl != null && !this.cache.has(frame.baseUrl))) {
          try {
            if (frame.baseUrl != null) {
              await this.cache.get(frame.baseUrl);
            }
            await this.cache.get(frame.url);
          } catch {
            this.scheduleTick();
            return;
          }
        }
        if (playing.value) {
          cursor.value = nextIndex;
        }
      }
    }
    this.scheduleTick();
  }
}
